import Phaser from "phaser";
import type { ShellLayout } from "../data/balance.js";
import type { AppActivity } from "./appLifecycle.js";
import { activityFromVisibility, activityFromIsActive } from "./appLifecycle.js";
import {
  applyActivity as applyLifecycleActivity,
  registerLifecycleHooks,
  setCrazyGamesBannerPaused,
  setupDailyReset as createDailyReset,
  setupCooldown as createCooldown,
  type LifecycleHooksHandle,
} from "./sceneLifecycleHooks.js";
import type { PieceRandomSource } from "../data/modulePieces.js";
import type { ModuleBrickSaveData } from "../models/ModuleBrickSaveData.js";
import type { DailyResetService } from "@yjsgames/daily-reset";
import type { CooldownService } from "@yjsgames/cooldown";
import type { DomainControllers } from "../app/createControllers.js";
import { contextOf } from "../app/contextOf.js";
import { createInitialProgress } from "../app/saveSwap.js";
import type { GameContext } from "../app/GameContext.js";
import type { ShellSystems } from "../app/createShellSystems.js";
import type { TabRouter } from "../app/TabRouter.js";
import { allPieceUnlockChapters } from "../ui/codexRedDot.js";
import { ALL_MODULE_DEFINITIONS } from "../data/moduleDefinitions.js";
import { TabRefresher } from "../ui/TabRefresher.js";
import { ToastPresenter } from "../ui/ToastPresenter.js";
import { createDisposerBag, type DisposerBag } from "@yjsgames/boot";
import { GameApiRegistry, type GameApi } from "../app/GameApi.js";
import { installDiagGlobal } from "../app/diag.js";
import type { TutorialRunner } from "@yjsgames/phaser-tutorial";
import type { AutoPlaySnapshot } from "../app/buildAutoPlaySnapshot.js";
import { ShellFrame, SHELL_EXPAND_FRACTION } from "../ui/ShellFrame.js";
import type { BattleProcEvent } from "../ui/ModuleGridView.js";
import { setGameviewContentOffset, setGameviewVisibleFraction } from "../ui/sceneLayout.js";
import { ModalHost } from "../ui/ModalHost.js";
import { SceneReadiness } from "./SceneReadiness.js";
import { BattlefieldRunner } from "./BattlefieldRunner.js";
import { DungeonFlow } from "./DungeonFlow.js";
import type { GameSceneRefreshDeps } from "./gameSceneRefresh.js";
import { applyGameSceneLayout } from "./gameSceneLayout.js";
import {
  isBannerPreviewEnabled,
  toggleBannerPreview as toggleGameSceneBannerPreview,
} from "./gameSceneBannerPreview.js";
import { resolveDesignHeight } from "../game/responsiveDesign.js";
import { handleSynergyDiscovered } from "./gameSceneSynergyDiscovery.js";
import { createGameSceneRuntime } from "./gameSceneRuntime.js";
import { buildGameSceneUiRuntime } from "./gameSceneUiRuntime.js";
import { loadAndApplyGameScene, toGameSceneSaveData } from "./gameScenePersistence.js";
import { createGameSceneRefreshRuntime, type GameSceneRefreshRuntime } from "./gameSceneRefreshRuntime.js";
import { updateGameSceneFrame } from "./gameSceneFrameLoop.js";
import { registerGameSceneCoreApi } from "./gameSceneDevApi.js";
import {
  getGameSceneAutoPlaySnapshot,
  resetGameSceneSave,
  resetGameSceneTutorial,
  startGameSceneTutorial,
} from "./gameSceneDevActions.js";
import { FEATURES, type FeatureFlags } from "../env/featureFlags.js";
import { installSceneTextResolutionPatch } from "../ui/uiTokens.js";
import { CHEAT_ENABLED, FORCE_TARGET_FPS } from "../env/appFlavor.js";
import { PERF_OVERLAY_AVAILABLE, PERF_OVERLAY_FORCED } from "../models/BrickSettings.js";
import { DESIGN } from "../data/balance.js";
import { showFatalErrorOverlay } from "../diag/globalErrorTrap.js";
import type { PerfTool } from "../diag/perfOverlay.js";
import { AppTimerOverlay } from "../ui/AppTimerOverlay.js";
import { resolveTargetFps, createAdaptiveFps, applyTargetFps as applyGameFps, type AdaptiveFps, type TargetFpsSetting } from "@yjsgames/perf";
import { t } from "../i18n/i18n.js";
import type { DialogueTrigger } from "../data/dialogueData.js";

export type { AutoPlaySnapshot } from "../app/buildAutoPlaySnapshot.js";

export class GameScene extends Phaser.Scene {
  static readonly KEY = "Game";
  static readonly EVENT_READY = "gamescene-ready";

  private controllers!: DomainControllers;
  private progress = createInitialProgress();
  private readonly codexUnlockChapters = allPieceUnlockChapters(ALL_MODULE_DEFINITIONS);
  private lastEnteredTab: string | null = null;
  private pieceRng: PieceRandomSource = Math.random;
  private dailyReset!: DailyResetService;
  private cooldown!: CooldownService;
  private readonly dailyResetHandlers = new Map<string, () => void>();
  private activeLayout!: ShellLayout;
  private bannerPreviewBox?: Phaser.GameObjects.Container;
  private frame!: ShellFrame;
  private gameviewExpanded = false;
  private runner!: BattlefieldRunner;
  private dungeonFlow!: DungeonFlow;
  private modals!: ModalHost;
  private shell!: ShellSystems;
  private cheatForceHouseAd = false;
  private perf!: PerfTool;
  private adaptiveFps?: AdaptiveFps;
  private appTimerOverlay?: AppTimerOverlay;
  private appTimerOverlayCheatEnabled = false;
  private pendingForceDialogueTriggers: DialogueTrigger[] = [];
  private forceDialogueTriggerHandler: ((trigger: DialogueTrigger) => void) | null = null;

  api!: GameApi;
  private apiRegistry!: GameApiRegistry;
  private disposers!: DisposerBag;

  private tabRouter!: TabRouter;
  private features: FeatureFlags = FEATURES;
  private readonly readiness = new SceneReadiness();
  private refresh!: GameSceneRefreshRuntime;

  get bootReady(): boolean {
    return this.readiness.canRefresh;
  }
  private settingsToggle: () => void = () => {};
  private battleProc: (procs: readonly BattleProcEvent[], startFired: boolean) => void = () => {};
  private lastBoxResult = "";

  private tutorialRunner!: TutorialRunner;
  private tabs!: TabRefresher;
  private toasts!: ToastPresenter;

  private lifecycleActivity: AppActivity = "active";
  private crazyGamesBannerGameplayPaused = false;
  private lifecycleHooks?: LifecycleHooksHandle;
  private readonly onDevicePreviewChange = (): void => {
    if (typeof document === "undefined") return;
    const root = document.getElementById("game-root");
    if (!root || root.clientWidth <= 0 || root.clientHeight <= 0) return;
    this.shell?.persistScheduler.flushNow();
    this.scale.getParentBounds();
    this.scale.setGameSize(
      DESIGN.width,
      resolveDesignHeight({ width: root.clientWidth, height: root.clientHeight }),
    );
    this.scene.restart();
  };

  private applyActivity(next: AppActivity): void {
    this.lifecycleActivity = applyLifecycleActivity(
      {
        activity: this.lifecycleActivity,
        setMasterMuted: () => {},
        setLoopSleep: (s) => { if (s) this.game.loop.sleep(); else this.game.loop.wake(); },
        flushSave: () => this.shell.persistScheduler.flushNow(),
      },
      next,
    );
    this.syncMasterMuted();
  }

  constructor() {
    super(GameScene.KEY);
  }

  private resetTransientState(): void {
    this.progress = createInitialProgress();
    this.cheatForceHouseAd = false;
    this.lastBoxResult = "";
    this.lifecycleActivity = "active";
    this.crazyGamesBannerGameplayPaused = false;
    this.gameviewExpanded = false;
    setGameviewContentOffset(this, 0);
    setGameviewVisibleFraction(this, 1);
  }

  create(): void {
    this.readiness.reset();
    this.resetTransientState();
    this.features =
      (this.registry.get("featureFlags") as FeatureFlags | undefined) ?? FEATURES;
    this.applyShellLayout();
    installDiagGlobal();
    this.disposers = createDisposerBag();
    this.apiRegistry = new GameApiRegistry();
    this.dailyResetHandlers.clear();
    this.refresh = createGameSceneRefreshRuntime(() => this.refreshDeps());
    installSceneTextResolutionPatch(this);

    const runtime = createGameSceneRuntime({
      scene: this,
      ctx: () => this.gameContext(),
      features: this.features,
      controllers: () => this.controllers,
      progress: () => this.progress,
      setControllers: (next) => {
        this.controllers = next;
      },
      setLastEnteredTab: (tab) => {
        this.lastEnteredTab = tab;
      },
      lastEnteredTab: () => this.lastEnteredTab,
      setBoxResult: (m) => {
        this.lastBoxResult = m;
      },
      pieceRng: () => this.pieceRng,
      cheatForceHouseAd: () => this.cheatForceHouseAd,
      toggleForceHouseAd: () => {
        this.cheatForceHouseAd = !this.cheatForceHouseAd;
        return this.cheatForceHouseAd;
      },
      dailyReset: () => this.dailyReset,
      cooldown: () => this.cooldown,
      readinessCanRefresh: () => this.readiness.canRefresh,
      registryGet: (key) => this.registry.get(key),
      frame: () => this.frame,
      gameviewExpanded: () => this.gameviewExpanded,
      toSaveData: () => this.toSaveData(),
      invalidateUi: () => this.invalidateUi(),
      refreshTabLocks: () => this.refresh.tabLocks(),
      forceDialogueTrigger: (trigger) => this.forceDialogueTrigger(trigger),
      refreshTabRedDots: () => this.refresh.tabRedDots(),
      refreshOnTabEnter: () => this.refresh.onTabEnter(),
      refreshActiveTab: () => this.refresh.activeTab(),
      refreshAll: () => this.refresh.all(),
      refreshHud: () => this.refresh.hud(),
      persist: () => this.persist(),
      persistNow: () => this.persistNow(),
      toggleBannerPreview: () => this.toggleBannerPreview(),
      bannerPreviewEnabled: () => this.bannerPreviewEnabled(),
      toggleAppTimerOverlay: () => this.toggleAppTimerOverlay(),
      appTimerOverlayEnabled: () => this.appTimerOverlayCheatEnabled,
      applyTargetFps: (setting) => this.applyTargetFps(setting),
      pauseGameplayForAd: () => this.setCrazyGamesBannerGameplayPaused(true),
      resumeGameplayForAd: () => this.setCrazyGamesBannerGameplayPaused(false),
      battleProc: () => this.battleProc,
      onSynergyDiscovered: (id) => this.onSynergyDiscovered(id),
    });
    this.controllers = runtime.controllers;
    this.modals = runtime.modals;
    this.tabs = runtime.tabs;
    this.toasts = runtime.toasts;
    this.tabRouter = runtime.tabRouter;
    this.shell = runtime.shell;
    this.runner = runtime.runner;
    this.tutorialRunner = runtime.tutorialRunner;
    this.dungeonFlow = runtime.dungeonFlow;
    this.registerDevicePreviewHooks();

    this.time.addEvent({
      delay: 250,
      loop: true,
      callback: () => this.refresh.activeTab(),
    });
    this.time.addEvent({
      delay: 30000,
      loop: true,
      callback: () => this.persist(),
    });
    this.registerLifecycleSaveHooks();
    this.registerCoreApi();

    void this.loadAndApply().catch((error: unknown) => {
      const message = error instanceof Error ? error.stack ?? error.message : String(error);
      console.error("[GameScene] loadAndApply failed", error);
      if (typeof document !== "undefined") showFatalErrorOverlay(`[GameScene] loadAndApply failed\n${message}`);
    });
  }

  private registerCoreApi(): void {
    registerGameSceneCoreApi({
      apiRegistry: this.apiRegistry,
      getAutoPlaySnapshot: () => this.getAutoPlaySnapshot(),
      tabRouter: this.tabRouter,
      resetSave: () => this.resetSave(),
      shell: this.shell,
      togglePerfOverlay: () => this.togglePerfOverlay(),
      perf: () => this.perf,
      startTutorial: (id) => this.startTutorial(id),
      resetTutorial: (id) => this.resetTutorial(id),
      dungeonFlow: () => this.dungeonFlow,
    });
  }

  private togglePerfOverlay(): boolean {
    if (PERF_OVERLAY_FORCED) {
      this.perf.setEnabled(true);
      this.shell.settings.setPerfOverlayEnabledTo(true);
      return true;
    }
    if (!PERF_OVERLAY_AVAILABLE) {
      this.perf.setEnabled(false);
      this.shell.settings.setPerfOverlayEnabledTo(false);
      return false;
    }
    const on = this.perf.toggle();
    this.shell.settings.setPerfOverlayEnabledTo(on);
    return on;
  }

  private toggleGameviewExpand(): void {
    this.gameviewExpanded = !this.gameviewExpanded;
    const { offset, content } = this.frame.setExpanded(this.gameviewExpanded);
    setGameviewContentOffset(this, offset);
    setGameviewVisibleFraction(this, this.gameviewExpanded ? SHELL_EXPAND_FRACTION : 1);
    this.runner.applyGameviewOffset(offset);
    this.tabs.resize(this.tabRouter.current(), content);
  }

  override update(): void {
    updateGameSceneFrame({
      scene: this,
      readiness: this.readiness,
      perf: this.perf,
      frame: this.frame,
      runner: this.runner,
      dungeonFlow: this.dungeonFlow,
      crazyGamesBannerGameplayPaused: this.crazyGamesBannerGameplayPaused,
      adaptiveFps: this.adaptiveFps,
      refreshHud: () => this.refresh.hud(),
      onAutoFpsAdjusted: (fps) => {
        this.toasts.showToast(t("settings.targetFps.autoAdjusted", { fps }));
      },
    });
    this.appTimerOverlay?.update();
  }

  private setCrazyGamesBannerGameplayPaused(paused: boolean): void {
    this.crazyGamesBannerGameplayPaused = setCrazyGamesBannerPaused(
      {
        setPaused: (p) => { this.runner?.setPaused(p); this.dungeonFlow?.setPaused(p); },
        pausePhysics: () => this.physics.world.pause(),
        resumePhysics: () => this.physics.world.resume(),
      },
      this.crazyGamesBannerGameplayPaused,
      paused,
    );
    this.syncMasterMuted();
    this.appTimerOverlay?.setPaused(this.crazyGamesBannerGameplayPaused);
  }

  private syncMasterMuted(): void {
    this.controllers.soundController.setMasterMuted(
      this.lifecycleActivity === "background" || this.crazyGamesBannerGameplayPaused,
    );
  }

  private applyTargetFps(setting: TargetFpsSetting): void {
    const effectiveSetting = FORCE_TARGET_FPS ?? setting;
    const target = resolveTargetFps(effectiveSetting, 240);
    applyGameFps(this.game, target);
    this.adaptiveFps =
      effectiveSetting === "auto" ? createAdaptiveFps({ displayHz: 240, start: target }) : undefined;
  }

  resetSave(): void {
    resetGameSceneSave({
      shell: this.shell,
      controllers: this.controllers,
      registryGet: (key) => this.registry.get(key),
      restart: () => this.scene.restart(),
    });
  }

  startTutorial(scenarioId = "main"): void {
    startGameSceneTutorial(this.tutorialRunner, scenarioId);
  }

  resetTutorial(scenarioId = "main"): void {
    resetGameSceneTutorial({
      progress: this.progress,
      persist: () => this.persist(),
      runner: this.tutorialRunner,
    }, scenarioId);
  }

  getAutoPlaySnapshot(): AutoPlaySnapshot {
    return getGameSceneAutoPlaySnapshot({
      ctx: this.gameContext(),
      shell: this.shell,
      dailyReset: () => this.dailyReset,
      tabRouter: this.tabRouter,
      modals: this.modals,
      runner: this.runner,
      dungeonFlow: this.dungeonFlow,
      dailyShopData: () => this.refresh.dailyShopData(),
      tutorialRunner: this.tutorialRunner,
      guideQuestPanel: () => this.frame.guideQuestPanel.debugSnapshot(),
    });
  }

  private applyShellLayout(): void {
    const result = applyGameSceneLayout({
      scene: this,
      registry: this.registry,
      featureBannerEnabled: this.features.banner,
      adFree: this.progress.adFree,
      bannerPreviewEnabled: this.bannerPreviewEnabled(),
      previousBannerPreviewBox: this.bannerPreviewBox,
    });
    this.activeLayout = result.layout;
    this.bannerPreviewBox = result.bannerPreviewBox;
  }

  private bannerPreviewEnabled(): boolean {
    return isBannerPreviewEnabled(CHEAT_ENABLED);
  }

  private toggleBannerPreview(): boolean {
    return toggleGameSceneBannerPreview(CHEAT_ENABLED);
  }

  private toggleAppTimerOverlay(): boolean {
    this.appTimerOverlayCheatEnabled = !this.appTimerOverlayCheatEnabled;
    this.syncAppTimerOverlay();
    return this.appTimerOverlayCheatEnabled;
  }

  private syncAppTimerOverlay(): void {
    if (this.appTimerOverlayCheatEnabled) {
      this.appTimerOverlay ??= new AppTimerOverlay(this);
      this.appTimerOverlay.setPaused(this.crazyGamesBannerGameplayPaused);
      this.appTimerOverlay.update();
      return;
    }
    this.appTimerOverlay?.destroy();
    this.appTimerOverlay = undefined;
  }

  private async loadAndApply(): Promise<void> {
    await loadAndApplyGameScene({
      controllers: () => this.controllers,
      setControllers: (next) => {
        this.controllers = next;
      },
      progress: () => this.progress,
      setProgress: (next) => {
        this.progress = next;
      },
      shell: () => this.shell,
      tabRouter: () => this.tabRouter,
      tutorialRunner: () => this.tutorialRunner,
      runner: () => this.runner,
      applySettings: () => this.shell.settings.loadAndApply(),
      applyShellLayout: () => this.applyShellLayout(),
      buildUi: () => this.buildUi(),
      setupDailyReset: () => this.setupDailyReset(),
      setupCooldown: () => this.setupCooldown(),
      refreshTabLocks: () => this.refresh.tabLocks(),
      refreshAll: () => this.refresh.all(),
    });
  }

  private toSaveData(): ModuleBrickSaveData {
    return toGameSceneSaveData(this.controllers, this.progress, this.tabRouter.current());
  }

  private persist(): void {
    this.shell.persistScheduler.request();
  }

  private persistNow(): void {
    this.shell.persistScheduler.flushNow();
  }

  private setupDailyReset(): void {
    this.dailyReset = createDailyReset(
      () => this.progress.dailyResets,
      this.dailyResetHandlers,
      () => this.persist(),
    );
  }

  private setupCooldown(): void {
    this.cooldown = createCooldown(() => this.progress.cooldowns);
  }

  private registerLifecycleSaveHooks(): void {
    this.lifecycleHooks = registerLifecycleHooks({
      onVisibilityChange: () => {
        if (typeof document === "undefined") return;
        this.applyActivity(activityFromVisibility(document.visibilityState));
      },
      onPageHide: () => this.shell.persistScheduler.flushNow(),
      onAppStateChange: (isActive) => this.applyActivity(activityFromIsActive(isActive)),
    });
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.forceDialogueTriggerHandler = null;
      this.pendingForceDialogueTriggers = [];
      this.tutorialRunner?.stop();
      this.disposers.disposeAll();
      this.tabs.disposeAll();
      this.frame?.dispose();
      if (typeof window !== "undefined") {
        window.removeEventListener("devicepreviewchange", this.onDevicePreviewChange);
      }
      this.appTimerOverlay?.destroy();
      this.appTimerOverlay = undefined;
      this.lifecycleHooks?.dispose();
      this.lifecycleHooks = undefined;
    });
  }

  private registerDevicePreviewHooks(): void {
    if (typeof window === "undefined" || typeof document === "undefined") return;
    window.addEventListener("devicepreviewchange", this.onDevicePreviewChange);
  }

  private buildUi(): void {
    const { frame, api, perf } = buildGameSceneUiRuntime({
      scene: this,
      ctx: this.gameContext(),
      shell: this.shell,
      features: this.features,
      layout: this.activeLayout,
      progress: () => this.progress,
      apiRegistry: this.apiRegistry,
      disposers: this.disposers,
      modals: this.modals,
      tabs: this.tabs,
      tabRouter: this.tabRouter,
      toasts: this.toasts,
      readiness: this.readiness,
      runner: () => this.runner,
      dungeonFlow: () => this.dungeonFlow,
      dailyReset: () => this.dailyReset,
      cooldown: () => this.cooldown,
      registryGet: (key) => this.registry.get(key),
      getAutoPlaySnapshot: () => this.getAutoPlaySnapshot(),
      settingsToggle: () => this.settingsToggle(),
      boxResult: () => this.lastBoxResult,
      registerDailyReset: (key, onReset) => this.dailyResetHandlers.set(key, onReset),
      resetSave: () => this.resetSave(),
      startTutorial: (id) => this.startTutorial(id),
      resetTutorial: (id) => this.resetTutorial(id),
      toggleGameviewExpand: () => this.toggleGameviewExpand(),
      togglePerfOverlay: () => this.togglePerfOverlay(),
      setBattleProc: (fn) => {
        this.battleProc = fn;
      },
      setSettingsToggle: (fn) => {
        this.settingsToggle = fn;
      },
      applyTargetFps: (setting) => this.applyTargetFps(setting),
      cheatForceHouseAd: () => this.cheatForceHouseAd,
      tutorialGate: (key) => this.tutorialRunner?.isInteractionAllowed(key) ?? true,
      provideForceDialogueTriggerHandler: (handler) => this.provideForceDialogueTriggerHandler(handler),
      readyEvent: GameScene.EVENT_READY,
    });
    this.frame = frame;
    this.api = api;
    this.perf = perf;
    this.syncAppTimerOverlay();
    this.installDevDebugGlobal();
  }

  private installDevDebugGlobal(): void {
    if (!import.meta.env.DEV || typeof window === "undefined") return;
    const debug = {
      snapshot: () => this.getAutoPlaySnapshot(),
      guideQuestPanel: () => this.frame.guideQuestPanel.debugSnapshot(),
      refreshAll: () => this.refresh.all(),
    };
    const target = window as unknown as Record<string, unknown>;
    target["__moduleBrickGame"] = debug;
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      if (target["__moduleBrickGame"] === debug) delete target["__moduleBrickGame"];
    });
  }

  private gameContext(): GameContext {
    return contextOf(() => this.controllers);
  }

  private invalidateUi(): void {
    this.shell.uiRefresh.invalidate();
  }

  private forceDialogueTrigger(trigger: DialogueTrigger): void {
    if (!this.forceDialogueTriggerHandler) {
      this.pendingForceDialogueTriggers.push(trigger);
      return;
    }
    this.forceDialogueTriggerHandler(trigger);
  }

  private provideForceDialogueTriggerHandler(handler: (trigger: DialogueTrigger) => void): void {
    this.forceDialogueTriggerHandler = handler;
    const pending = this.pendingForceDialogueTriggers.splice(0);
    for (const trigger of pending) handler(trigger);
  }

  private refreshDeps(): GameSceneRefreshDeps {
    return {
      readiness: this.readiness,
      modals: this.modals,
      tabRouter: this.tabRouter,
      tabs: this.tabs,
      frame: this.frame,
      controllers: this.controllers,
      progress: this.progress,
      shell: this.shell,
      dailyReset: this.dailyReset,
      codexUnlockChapters: this.codexUnlockChapters,
    };
  }

  private onSynergyDiscovered(synergyId: string): void {
    handleSynergyDiscovered(synergyId, {
      grantReward: (reward) => this.shell.rewards.grantSynergyReward(reward),
      showToast: (message) => this.toasts.showToast(message),
      persistNow: () => this.persistNow(),
      refreshHud: () => this.refresh.hud(),
    });
  }
}
